import { useState } from 'react';
import { X } from 'lucide-react';
import { cn } from '@/lib/cn';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import type { Difficulty, ProblemStatus } from '@/lib/api/admin-problems';

interface Props {
  difficulty: Difficulty;
  tags: string[];
  companyTags: string[];
  companyFrequency: Record<string, number>;
  timeLimitMs: number;
  memoryLimitMb: number;
  slug?: string;
  status: ProblemStatus;
  onChange: (field: string, value: unknown) => void;
}

const statusVariant: Record<string, string> = {
  published: 'border border-emerald-500 text-emerald-600 bg-transparent',
  draft:     'border border-orange-400 text-orange-500 bg-transparent',
  archived:  'border border-gray-400 text-gray-500 bg-transparent',
};

function TagInput({
  tags,
  placeholder,
  chipClass,
  onAdd,
  onRemove,
}: {
  tags: string[];
  placeholder: string;
  chipClass: string;
  onAdd: (tag: string) => void;
  onRemove: (tag: string) => void;
}) {
  const [input, setInput] = useState('');

  function handleKey(e: React.KeyboardEvent<HTMLInputElement>) {
    if ((e.key === 'Enter' || e.key === ',') && input.trim()) {
      e.preventDefault();
      onAdd(input.trim());
      setInput('');
    }
  }

  return (
    <div className="flex flex-wrap gap-1">
      {tags.map(tag => (
        <span key={tag} className={cn('flex items-center gap-0.5 px-2 py-0.5 rounded-full text-[11px]', chipClass)}>
          {tag}
          <button onClick={() => onRemove(tag)} className="opacity-60 hover:opacity-100 ml-0.5">
            <X className="h-2.5 w-2.5" />
          </button>
        </span>
      ))}
      <input
        value={input}
        onChange={e => setInput(e.target.value)}
        onKeyDown={handleKey}
        placeholder={placeholder}
        className="text-[12px] text-muted-foreground bg-transparent outline-none min-w-[80px] py-0.5"
      />
    </div>
  );
}

export function ProblemMetadataSidebar({
  difficulty, tags, companyTags, companyFrequency, timeLimitMs, memoryLimitMb, slug, status, onChange,
}: Props) {
  function addTag(tag: string) {
    if (!tags.includes(tag)) onChange('tags', [...tags, tag]);
  }
  function removeTag(tag: string) {
    onChange('tags', tags.filter(t => t !== tag));
  }
  function addCompanyTag(tag: string) {
    if (!companyTags.includes(tag)) {
      onChange('company_tags', [...companyTags, tag]);
      onChange('company_frequency', { ...companyFrequency, [tag]: 1 });
    }
  }
  function removeCompanyTag(tag: string) {
    onChange('company_tags', companyTags.filter(t => t !== tag));
    const { [tag]: _removed, ...rest } = companyFrequency;
    onChange('company_frequency', rest);
  }
  function setCompanyFrequency(tag: string, freq: number) {
    onChange('company_frequency', { ...companyFrequency, [tag]: Math.max(1, freq) });
  }

  return (
    <div className="flex flex-col gap-5 p-4 border-r bg-muted/5 min-h-full">
      {/* Status badge */}
      <div>
        <p className="text-[10px] font-semibold text-muted-foreground uppercase tracking-wide mb-1.5">Status</p>
        <span className={cn('text-[11px] font-semibold px-2.5 py-1 rounded-full border', statusVariant[status] ?? statusVariant.draft)}>
          {status.charAt(0).toUpperCase() + status.slice(1)}
        </span>
      </div>

      {/* Difficulty */}
      <div>
        <p className="text-[10px] font-semibold text-muted-foreground uppercase tracking-wide mb-1.5">Difficulty</p>
        <Select value={difficulty} onValueChange={v => onChange('difficulty', v)}>
          <SelectTrigger className="h-8 text-[12px]">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="easy">Easy</SelectItem>
            <SelectItem value="medium">Medium</SelectItem>
            <SelectItem value="hard">Hard</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Tags */}
      <div>
        <p className="text-[10px] font-semibold text-muted-foreground uppercase tracking-wide mb-1.5">
          Tags <span className="normal-case font-normal">(Enter to add)</span>
        </p>
        <div className="border rounded-md p-2 bg-white min-h-[40px]">
          <TagInput
            tags={tags}
            placeholder="e.g. Array"
            chipClass="bg-blue-50 text-blue-700"
            onAdd={addTag}
            onRemove={removeTag}
          />
        </div>
      </div>

      {/* Company Tags */}
      <div>
        <p className="text-[10px] font-semibold text-muted-foreground uppercase tracking-wide mb-1.5">
          Company Tags <span className="normal-case font-normal">(Enter to add)</span>
        </p>
        <div className="border rounded-md p-2 bg-white min-h-[40px] flex flex-col gap-1.5">
          {companyTags.map(tag => (
            <div key={tag} className="flex items-center justify-between gap-2 px-2 py-0.5 rounded-full text-[11px] bg-purple-50 text-purple-700">
              <span className="truncate">{tag}</span>
              <div className="flex items-center gap-1 shrink-0">
                <span className="text-muted-foreground normal-case">asked</span>
                <input
                  type="number"
                  min={1}
                  value={companyFrequency[tag] ?? 1}
                  onChange={e => setCompanyFrequency(tag, Number(e.target.value))}
                  className="w-10 text-center border rounded px-1 py-0.5 text-[11px] bg-white"
                />
                <span className="text-muted-foreground normal-case">x</span>
                <button onClick={() => removeCompanyTag(tag)} className="opacity-60 hover:opacity-100">
                  <X className="h-2.5 w-2.5" />
                </button>
              </div>
            </div>
          ))}
          <TagInput
            tags={[]}
            placeholder="e.g. TCS"
            chipClass="bg-purple-50 text-purple-700"
            onAdd={addCompanyTag}
            onRemove={removeCompanyTag}
          />
        </div>
      </div>

      {/* Limits */}
      <div>
        <p className="text-[10px] font-semibold text-muted-foreground uppercase tracking-wide mb-1.5">Limits</p>
        <div className="flex flex-col gap-2">
          <div className="flex items-center justify-between text-[12px]">
            <span className="text-muted-foreground">Time (ms)</span>
            <input
              type="number"
              value={timeLimitMs}
              onChange={e => onChange('time_limit_ms', Number(e.target.value))}
              className="w-20 text-right border rounded-md px-2 py-1 text-[12px] bg-white"
            />
          </div>
          <div className="flex items-center justify-between text-[12px]">
            <span className="text-muted-foreground">Memory (MB)</span>
            <input
              type="number"
              value={memoryLimitMb}
              onChange={e => onChange('memory_limit_mb', Number(e.target.value))}
              className="w-20 text-right border rounded-md px-2 py-1 text-[12px] bg-white"
            />
          </div>
        </div>
      </div>

      {/* Slug (read-only) */}
      {slug && (
        <div>
          <p className="text-[10px] font-semibold text-muted-foreground uppercase tracking-wide mb-1.5">Slug</p>
          <p className="text-[11px] text-muted-foreground break-all font-mono">{slug}</p>
        </div>
      )}
    </div>
  );
}
